import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { benefitService } from '../../services/benefit.service';
import { partnerService } from '../../services/partner.service';
import { Benefit, Partner } from '../../types';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Loading } from '../../components/ui/Loading';

export const BenefitsPage = () => {
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState<Benefit | null>(null);
  const [formData, setFormData] = useState({
    partnerId: '',
    description: '',
    membershipType: 'INDIVIDUAL',
    discountValue: 0,
    enabled: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [benefitsData, partnersData] = await Promise.all([
        benefitService.getAllBenefits(),
        partnerService.getAllPartners()
      ]);
      setBenefits(benefitsData);
      setPartners(partnersData);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.partnerId) {
        toast.error('Please select a partner');
        return;
      }

      const data = {
        partnerId: formData.partnerId,
        description: formData.description,
        membershipType: formData.membershipType as Benefit['membershipType'],
        discountValue: Number(formData.discountValue),
        enabled: formData.enabled
      };

      if (editingBenefit) {
        await benefitService.updateBenefit(editingBenefit.id, data);
        toast.success('Benefit updated successfully');
      } else {
        await benefitService.createBenefit(data);
        toast.success('Benefit created successfully');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save benefit');
    }
  };

  const resetForm = () => {
    setFormData({
      partnerId: '',
      description: '',
      membershipType: 'INDIVIDUAL',
      discountValue: 0,
      enabled: true
    });
    setEditingBenefit(null);
  };

  const handleEdit = (benefit: Benefit) => {
    setEditingBenefit(benefit);
    setFormData({
      partnerId: benefit.partnerId || '',
      description: benefit.description || '',
      membershipType: benefit.membershipType,
      discountValue: benefit.discountValue || 0,
      enabled: benefit.enabled !== undefined ? benefit.enabled : true
    });
    setShowModal(true);
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Benefit Management</h1>
          <p className="text-gray-600">Configure membership benefits and discounts</p>
        </div>
        <Button onClick={() => setShowModal(true)}>Add Benefit</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {benefits.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">No benefits found. Click "Add Benefit" to create one.</p>
          </div>
        ) : (
          benefits.map((benefit) => (
            <div key={benefit.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">{benefit.partner?.name || 'Unknown Partner'}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${benefit.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                  {benefit.enabled ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-4">{benefit.description}</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium">{benefit.membershipType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Discount:</span>
                  <span className="font-medium">{benefit.discountValue}%</span>
                </div>
              </div>
              <div className="mt-4">
                <Button variant="secondary" size="sm" onClick={() => handleEdit(benefit)} className="w-full">
                  Edit
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingBenefit ? 'Edit Benefit' : 'Add Benefit'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Partner"
            value={formData.partnerId}
            onChange={(e) => setFormData({ ...formData, partnerId: e.target.value })}
            options={[
              { value: '', label: 'Select Partner' },
              ...partners.map(p => ({ value: p.id, label: p.name }))
            ]}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
              placeholder="e.g. 20% discount on all consultations"
              required
            />
          </div>

          <Select
            label="Membership Type"
            value={formData.membershipType}
            onChange={(e) => setFormData({ ...formData, membershipType: e.target.value })}
            options={[
              { value: 'INDIVIDUAL', label: 'Individual' },
              { value: 'FAMILY', label: 'Family' },
              { value: 'CORPORATE', label: 'Corporate' }
            ]}
          />

          <Input
            label="Discount Value (%)"
            type="number"
            value={formData.discountValue}
            onChange={(e) => setFormData({ ...formData, discountValue: parseInt(e.target.value) })}
            min="0"
            max="100"
            required
          />

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="enabled">Enabled</label>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editingBenefit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
